import { dinero, fechaHora, numero, traducirProblema, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import { Ayuda, AvisoError, Boton, Chip, IconoMas, Menu, useAvisos, type ElementoMenu } from '@tallerya/ui';
import { useRef, useState, type ChangeEvent } from 'react';
import { TIPOS_ACEPTADOS, useCambiosAdjuntos } from '../../componentes/adjuntos/datos';
import { usePermisos } from '../../permisos/usePermiso';
import {
  montoDe,
  useCambiosLinea,
  useLineasOrden,
  useMontosOrden,
  type CabeceraOrden,
  type LineaOrden,
  type MontosOrden,
} from './datos';
import { AgregarLinea, AsignarLinea, CantidadLinea, LigarLinea, QuitarLinea } from './VentanasDeLinea';
import { FijarTotal, PrecioLinea } from './VentanasDeMontos';

// La sección «Trabajo» de la ficha (#133), como en `190:9042`: las líneas de la
// orden —servicio, producto y anotación— con su casilla para cumplirlas.
//
//   Trabajo ⓘ                                   [+ Agregar línea]
//   2 de 4 cumplidas · 1 anotación
//   ☑ [Servicio] Cambio de pastillas de freno                 •••
//       Dictado: «cambio de balatas»
//       Cumplida por Carlos Méndez · 10:20
//
// - **La casilla es un toque**: cumple a nombre de quien entra, y destildarla
//   reabre. Pide `order.line.complete`; lo demás —agregar, cantidad, asignar,
//   ligar, quitar— pide `order.edit` y vive en «+ Agregar línea» y en el «•••».
// - Una anotación no es trabajo: no tiene casilla ni cantidad. Su «•••» ofrece
//   ligarla al catálogo, que la convierte en el servicio o el producto que era.
// - **«Agregar foto» en el «•••»** sube la foto a esa línea (#140), con `order.edit`.
//   Se ve en la sección «Fotos», con el nombre de la línea debajo.
//
// **Los montos (#137)**, solo con `order.amount.view`: el de cada línea a la
// derecha —o «Sin precio»— y el total al pie. Se leen de su propio endpoint
// (#136) y se juntan con las líneas por id. Con `order.amount.edit`, el «•••»
// ofrece cambiar el precio —salvo con la orden finalizada: los montos de las
// líneas están congelados— y el total tiene su «Cambiar total».
//
// Lo que no sigue al Figma, a propósito:
//
//   - **«Fijado a mano»**, en gris, junto a un total que alguien fijó: ya no se
//     marca incompleto (#137), y quien cobra podría extrañar que no cuadre con
//     la suma de las líneas.
//   - La anotación no dice quién la escribió ni cuándo: la lista de líneas no lo
//     trae. Sale en el historial (#141).

/** El nombre de la línea como se lee en la lista: el del catálogo, o lo que se escribió. */
export const nombreDe = (linea: LineaOrden) => linea.catalogItemName ?? linea.rawText ?? '';

type Abierta =
  | { tipo: 'agregar' | 'anotar' | 'total' }
  | { tipo: 'cantidad' | 'asignar' | 'ligar' | 'quitar' | 'precio'; linea: LineaOrden }
  | null;

export function SeccionTrabajo({ orden }: { orden: CabeceraOrden }) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const { puede } = usePermisos();
  const id = String(orden.id);
  const consulta = useLineasOrden(id);
  const cambios = useCambiosLinea(id);

  const [abierta, setAbierta] = useState<Abierta>(null);
  // Lo que rechazó un toque —la casilla, quitar—: no tienen ventana donde
  // decirlo. Se va con el próximo cambio que salga bien.
  const [problema, setProblema] = useState<string | null>(null);
  /** La línea con un toque en camino: su casilla espera. */
  const [enCurso, setEnCurso] = useState<string | null>(null);
  const fotos = useCambiosAdjuntos({ tipo: 'orden', id });
  const selectorFoto = useRef<HTMLInputElement>(null);
  /** La línea a la que va la foto que se está eligiendo. Ref y no estado: el selector responde después. */
  const fotoPara = useRef<LineaOrden | null>(null);

  const edita = puede('order.edit');
  const cumple = puede('order.line.complete');
  const veMontos = puede('order.amount.view');
  const editaMontos = veMontos && puede('order.amount.edit');
  const montos = useMontosOrden(id, veMontos);
  // Los montos que se pueden mostrar: con permiso y si la última lectura no
  // falló. Si una recarga falla, React Query conserva los anteriores, y esos no
  // se dibujan ni se ofrecen para editar.
  const listos = veMontos && !montos.isError ? montos.data : undefined;
  const lineas = consulta.data ?? [];
  const trabajo = lineas.filter((l) => l.kind !== 'note');
  const anotaciones = lineas.length - trabajo.length;

  const textoDe = (p: ProblemaApi) => (p.code ? traducirProblema(p, t) : t('ui.formulario.aviso.sinRespuesta'));

  /** Un toque sin ventana: si la API lo rechaza, lo dice el aviso de la sección. */
  async function tocar(linea: LineaOrden, accion: () => Promise<ProblemaApi | undefined>, hecho?: string) {
    setEnCurso(String(linea.id));
    try {
      const rechazo = await accion();
      setProblema(rechazo ? textoDe(rechazo) : null);
      if (!rechazo && hecho) avisar({ titulo: hecho });
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
    } finally {
      setEnCurso(null);
    }
  }

  /** Las fotos elegidas, a la línea del «•••», de a una como la galería. Para en la primera que falla. */
  async function subirFotos(evento: ChangeEvent<HTMLInputElement>) {
    const archivos = [...(evento.target.files ?? [])];
    // Se limpia para que volver a elegir el mismo archivo dispare el cambio.
    evento.target.value = '';
    const linea = fotoPara.current;
    if (!linea || archivos.length === 0) return;

    await tocar(
      linea,
      async () => {
        for (const archivo of archivos) {
          const rechazo = await fotos.subir(archivo, String(linea.id));
          if (rechazo) return rechazo;
        }
        return undefined;
      },
      t('admin.ordenes.lineas.aviso.fotoAgregada', { n: archivos.length, linea: nombreDe(linea) }),
    );
  }

  const cerrar = () => setAbierta(null);
  const lineaDe = (tipo: 'cantidad' | 'asignar' | 'ligar' | 'quitar' | 'precio') =>
    abierta?.tipo === tipo ? abierta.linea : null;

  /**
   * Lo que dicen los montos de una línea, o `undefined` si no se sabe: sin
   * permiso, una anotación, o una línea que todavía no llegó en los montos —las
   * dos consultas se recargan por separado—. Lo que no se sabe no se dibuja
   * como «Sin precio», ni se ofrece cambiarlo: la ventana abriría vacía.
   */
  const montosDe = (linea: LineaOrden) =>
    linea.kind !== 'note' ? listos?.lines.find((m) => String(m.id) === String(linea.id)) : undefined;
  const montoDeLinea = (linea: LineaOrden) => {
    const suyos = montosDe(linea);
    return suyos ? montoDe(suyos.amount) : undefined;
  };
  const precioAbierto = lineaDe('precio');

  const resumen = [
    trabajo.length > 0 &&
      t('admin.ordenes.lineas.cumplidas', { n: trabajo.length, hechas: trabajo.filter((l) => l.isDone).length }),
    anotaciones > 0 && t('admin.ordenes.lineas.anotaciones', { n: anotaciones }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="orden__seccion">
      <header className="orden__seccion-cabecera">
        <div>
          <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
            <h2>{t('admin.ordenes.lineas.titulo')}</h2>
            <Ayuda tema={t('admin.ordenes.lineas.titulo')} texto={t('admin.ordenes.lineas.info')} tono="titulo" />
          </div>
          {resumen && <p className="tipos__resumen">{resumen}</p>}
        </div>
        {edita && (
          <div className="orden__seccion-acciones">
            <Boton variante="secundario" icono={<IconoMas />} onClick={() => setAbierta({ tipo: 'agregar' })}>
              {t('admin.ordenes.lineas.agregar')}
            </Boton>
            <Boton variante="secundario" icono={<IconoMas />} onClick={() => setAbierta({ tipo: 'anotar' })}>
              {t('admin.ordenes.lineas.anotar')}
            </Boton>
          </div>
        )}
      </header>

      {problema && <AvisoError aviso={{ titulo: t('admin.ordenes.lineas.noSePudo'), texto: problema }} />}

      {consulta.isPending ? (
        <p className="ficha__nota">{t('app.cargando')}</p>
      ) : consulta.isError ? (
        <div className="ficha__vacio">
          <p className="ficha__vacio-titulo">{t('ui.listado.error.titulo')}</p>
          <Boton variante="secundario" onClick={() => void consulta.refetch()}>
            {t('app.reintentar')}
          </Boton>
        </div>
      ) : lineas.length === 0 ? (
        <div className="ficha__vacio">
          <p className="ficha__vacio-titulo">{t('admin.ordenes.lineas.vacio')}</p>
          <p>{t('admin.ordenes.lineas.vacioTexto')}</p>
        </div>
      ) : (
        <ul className="orden__lineas">
          {lineas.map((linea) => (
            <Linea
              key={String(linea.id)}
              linea={linea}
              puedeCumplir={cumple}
              esperando={enCurso === String(linea.id)}
              onAlternar={() =>
                void tocar(linea, () =>
                  linea.isDone ? cambios.reabrir(String(linea.id)) : cambios.cumplir(String(linea.id)),
                )
              }
              monto={montoDeLinea(linea)}
              moneda={listos?.currencyCode ?? null}
              acciones={[
                ...(editaMontos && montosDe(linea) && !orden.statusIsFinal
                  ? [
                      {
                        etiqueta: t('admin.ordenes.montos.cambiarPrecio'),
                        onSeleccionar: () => setAbierta({ tipo: 'precio', linea }),
                      },
                    ]
                  : []),
                ...(edita
                  ? [
                      // Toda línea sin ítem se liga: la anotación y la línea libre (#162).
                      ...(linea.catalogItemId
                        ? []
                        : [
                            {
                              etiqueta: t('admin.ordenes.lineas.ligar'),
                              onSeleccionar: () => setAbierta({ tipo: 'ligar', linea }),
                            },
                          ]),
                      ...(linea.kind === 'note'
                        ? []
                        : [
                            {
                              etiqueta: t('admin.ordenes.lineas.cambiarCantidad'),
                              onSeleccionar: () => setAbierta({ tipo: 'cantidad', linea }),
                            },
                            {
                              etiqueta: t('admin.ordenes.lineas.asignar'),
                              onSeleccionar: () => setAbierta({ tipo: 'asignar', linea }),
                            },
                          ]),
                      {
                        etiqueta: t('admin.ordenes.lineas.agregarFoto'),
                        onSeleccionar: () => {
                          fotoPara.current = linea;
                          selectorFoto.current?.click();
                        },
                      },
                      { tipo: 'divisor' as const },
                      {
                        etiqueta: t('admin.ordenes.lineas.quitar'),
                        peligro: true,
                        onSeleccionar: () => setAbierta({ tipo: 'quitar', linea }),
                      },
                    ]
                  : []),
              ]}
            />
          ))}
        </ul>
      )}

      {veMontos && montos.isError && lineas.length > 0 && (
        <div className="ficha__vacio">
          <p className="ficha__vacio-titulo">{t('admin.ordenes.montos.error')}</p>
          <Boton variante="secundario" onClick={() => void montos.refetch()}>
            {t('app.reintentar')}
          </Boton>
        </div>
      )}

      {listos && lineas.length > 0 && (
        <PieDeMontos
          montos={listos}
          puedeCambiar={editaMontos && orden.statusCategory !== 'cancelled'}
          onCambiar={() => setAbierta({ tipo: 'total' })}
        />
      )}

      <input
        ref={selectorFoto}
        type="file"
        accept={TIPOS_ACEPTADOS}
        multiple
        hidden
        onChange={(e) => void subirFotos(e)}
      />
      <AgregarLinea
        abierta={abierta?.tipo === 'agregar' || abierta?.tipo === 'anotar'}
        modo={abierta?.tipo === 'anotar' ? 'anotacion' : 'trabajo'}
        orden={id}
        onCerrar={cerrar}
        onAgregada={() => setProblema(null)}
      />
      <CantidadLinea orden={id} linea={lineaDe('cantidad')} onCerrar={cerrar} onGuardada={() => setProblema(null)} />
      <AsignarLinea orden={orden} linea={lineaDe('asignar')} onCerrar={cerrar} onGuardada={() => setProblema(null)} />
      <LigarLinea orden={id} linea={lineaDe('ligar')} onCerrar={cerrar} onGuardada={() => setProblema(null)} />
      <PrecioLinea
        orden={id}
        linea={precioAbierto}
        precio={
          precioAbierto
            ? (listos?.lines.find((m) => String(m.id) === String(precioAbierto.id))?.unitPrice ?? null)
            : null
        }
        onCerrar={cerrar}
      />
      {/* La excepción a `listos`: una ventana ya abierta no se desmonta si una
          recarga falla, o se perdería lo escrito. Abrirla sí exige `listos`
          (el pie), y si sus montos quedaron viejos, la versión hace que la API
          responda «cambio simultáneo» en vez de guardar. */}
      {montos.data && (
        <FijarTotal
          abierta={abierta?.tipo === 'total'}
          orden={id}
          montos={montos.data}
          finalizada={orden.statusIsFinal}
          onCerrar={cerrar}
        />
      )}
      <QuitarLinea
        linea={lineaDe('quitar')}
        onCerrar={cerrar}
        onQuitar={async (linea) => {
          await tocar(
            linea,
            async () => {
              const rechazo = await cambios.quitar(String(linea.id));
              // Sus fotos se fueron con ella: la sección «Fotos» no las sigue mostrando.
              if (!rechazo) await fotos.refrescar();
              return rechazo;
            },
            t('admin.ordenes.lineas.aviso.quitada'),
          );
          cerrar();
        }}
      />
    </section>
  );
}

/** Un monto con la moneda del taller. Sin moneda no se inventa una: el número va solo. */
function useDinero(moneda: string | null) {
  const { idioma } = useTraduccion();
  return (valor: number) => (moneda ? dinero(valor, moneda, idioma) : numero(valor, idioma));
}

/** Lo que dice la marca junto al total, o nada. */
interface MarcaDelTotal {
  texto: string;
  /** `aviso` es el ámbar de «Total incompleto» en el Figma; `neutro`, una nota sin alarma. */
  tono: 'aviso' | 'neutro';
}

/**
 * La marca que acompaña al total, a la izquierda del pie.
 *
 * El servidor ya resolvió la regla de fondo: con el total fijado a mano,
 * `isTotalIncomplete` llega en falso (#137). Lo que se decide acá es qué se le
 * dice a quien cobra en cada caso.
 */
function marcaDelTotal(montos: MontosOrden, t: ReturnType<typeof useTraduccion>['t']): MarcaDelTotal | null {
  const sinPrecio = montos.lines.filter((l) => l.amount === null).length;

  // Fijado a mano no se marca incompleto (lo dice el servidor), pero sí se avisa:
  // quien cobra podría extrañar que no cuadre con la suma de las líneas.
  if (montos.totalIsOverridden) {
    return { texto: t('admin.ordenes.montos.fijado'), tono: 'neutro' };
  }

  if (montos.isTotalIncomplete) {
    return {
      texto: `${t('admin.ordenes.montos.incompleto')} · ${t('admin.ordenes.montos.lineasSinPrecio', { n: sinPrecio })}`,
      tono: 'aviso',
    };
  }

  return null;
}

/** El pie de «Trabajo»: la marca del total a la izquierda, y el total con su «Cambiar total». */
function PieDeMontos({
  montos,
  puedeCambiar,
  onCambiar,
}: {
  montos: MontosOrden;
  puedeCambiar: boolean;
  onCambiar: () => void;
}) {
  const { t } = useTraduccion();
  const formato = useDinero(montos.currencyCode);
  const total = montoDe(montos.totalAmount);
  const marca = marcaDelTotal(montos, t);

  return (
    <footer className="orden__total">
      <div className="orden__total-marca">
        {marca && <span className={`orden__marca orden__marca--${marca.tono}`}>{marca.texto}</span>}
      </div>
      <div className="orden__total-cifra">
        <span className="orden__total-etiqueta">{t('admin.ordenes.montos.total')}</span>
        <strong>{total === null ? '—' : formato(total)}</strong>
        {puedeCambiar && (
          <Boton variante="texto" onClick={onCambiar}>
            {t('admin.ordenes.montos.cambiarTotal')}
          </Boton>
        )}
      </div>
    </footer>
  );
}

function Linea({
  linea,
  puedeCumplir,
  esperando,
  onAlternar,
  monto,
  moneda,
  acciones,
}: {
  linea: LineaOrden;
  puedeCumplir: boolean;
  esperando: boolean;
  onAlternar: () => void;
  /** `undefined`: no se dibuja. `null`: la línea no tiene precio. */
  monto: number | null | undefined;
  moneda: string | null;
  acciones: ElementoMenu[];
}) {
  const formato = useDinero(moneda);
  const { t, idioma } = useTraduccion();
  const nombre = nombreDe(linea);
  const esAnotacion = linea.kind === 'note';
  const cantidad = linea.quantity === null ? 1 : Number(linea.quantity);

  // Un producto no se «cumple»: se entrega. Es la misma marca con otra palabra.
  const estado = esAnotacion
    ? null
    : linea.isDone
      ? [
          t(linea.kind === 'product' ? 'admin.ordenes.lineas.entregadaPor' : 'admin.ordenes.lineas.cumplidaPor', {
            persona: linea.completedByName ?? '',
          }),
          linea.completedAt && fechaHora(linea.completedAt, idioma),
        ]
          .filter(Boolean)
          .join(' · ')
      : linea.assignedToName
        ? t('admin.ordenes.lineas.asignadaA', { persona: linea.assignedToName })
        : t('admin.ordenes.lineas.pendiente');

  return (
    <li className={`orden__linea${linea.isDone ? ' orden__linea--hecha' : ''}`}>
      {/* La anotación no tiene casilla, pero guarda su lugar: los nombres quedan alineados. */}
      {esAnotacion ? (
        <span className="orden__linea-casilla" aria-hidden="true" />
      ) : (
        <input
          type="checkbox"
          className="orden__linea-casilla"
          checked={linea.isDone}
          disabled={!puedeCumplir || esperando}
          aria-busy={esperando}
          aria-label={t('admin.ordenes.lineas.cumplida', { linea: nombre })}
          onChange={onAlternar}
        />
      )}
      <div className="orden__linea-cuerpo">
        <p className="orden__linea-nombre">
          <Chip tono={linea.kind === 'service' ? 'marca' : 'neutro'}>{t(`admin.ordenes.lineas.clase.${linea.kind}`)}</Chip>
          <span>{nombre}</span>
          {cantidad !== 1 && <span className="orden__linea-cantidad">× {cantidad}</span>}
        </p>
        {/* Lo que se dictó, cuando la línea ya es del catálogo: es lo que permite revisar el reconocimiento. */}
        {linea.catalogItemId && linea.rawText && (
          <p className="orden__linea-detalle">{t('admin.ordenes.lineas.dictado', { texto: linea.rawText })}</p>
        )}
        {estado && <p className="orden__linea-detalle">{estado}</p>}
      </div>
      {monto !== undefined && (
        <span className={`orden__linea-monto${monto === null ? ' orden__linea-monto--sin' : ''}`}>
          {monto === null ? t('admin.ordenes.montos.sinPrecio') : formato(monto)}
        </span>
      )}
      {acciones.length > 0 && (
        <Menu
          elementos={acciones}
          disparador={({ alternar, atributos }) => (
            <Boton
              variante="texto"
              soloIcono
              aria-label={t('admin.ordenes.lineas.acciones', { linea: nombre })}
              onClick={alternar}
              {...atributos}
            >
              •••
            </Boton>
          )}
        />
      )}
    </li>
  );
}
